import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import _ from "lodash";
import { Observable, forkJoin, of } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { FormatService } from "~app/common/services/format.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { IProduktdefinitionBasdata, IProduktinstruktionSearchModel, ITreeSelectionItem } from "../models/ProduktdefinitionModels";
import { ProduktinstruktionService } from "../services/produktinstruktion.service";
import { ProduktinstruktionUtils } from "../services/produktinstruktionUtils";

@Component({
    selector: "rask-produktinstruktioner-search",
    templateUrl: "./produktinstruktioner-search.component.html",
    styleUrls: ["./produktinstruktioner-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        TreeSelectorComponent,
        MultiSelectorComponent,
        RadiobuttonGroupComponent,
        SubmitButtonComponent,
        ButtonComponent
    ]
})
export class ProduktinstruktionerSearchComponent implements OnInit {
    activetypes: IRadiobuttonItem[] = [
        { key: "true",
            value: "Ja" },
        { key: "false",
            value: "Nej" },
        { key: "",
            value: "Alla" }
    ];
    form: UntypedFormGroup;
    produktinstruktionBasdata: IProduktdefinitionBasdata;
    regionerItems: ITreeSelectionItem[];
    searchParms: IProduktinstruktionSearchModel;
    tradslagsList: Observable<string[]>;

    private readonly busyService = inject(BusyService);
    private readonly formBinder = inject(FormBinderService);
    private readonly formatService = inject(FormatService);
    private readonly produktinstruktionSearchService = inject(ProduktinstruktionService);
    private readonly webinstallningarService = inject(WebinstallningarService);
    private readonly wik = "produktinstruktion.searchparams";

    constructor() {
        this.createEmptySearch();
    }

    ngOnInit(): void {
        forkJoin([
            this.webinstallningarService.getAsync<IProduktinstruktionSearchModel>(this.wik),
            this.produktinstruktionSearchService.getProduktinstruktionBasData()
        ]).subscribe((result) => {
            const [
                webinstallning,
                basData
            ] = result;
            this.regionerItems = ProduktinstruktionUtils.getRegioner(basData.regioner);
            this.produktinstruktionBasdata = basData;
            this.tradslagsList = of(basData.tradslagsgrupper.map(x => x.namn));
            this.copyWebbInstallningToSearchObject(webinstallning);
            this.form = this.formBinder.CreateGroup(this.searchParms, undefined, undefined, false);
            this.form.get("aktiv").setValue(this.formatService.booleanToString(this.searchParms.aktiv));
            this.search();
        });
    }

    regionerSortExpression = (item: ITreeSelectionItem): number => item.sekvensnummer;

    reset() {
        this.createEmptySearch();
        this.formBinder.reBindForm(this.form, this.searchParms);
    }

    search() {
        this.mapFromForm();

        const params = _.clone(this.searchParms);
        params.regioner = ProduktinstruktionUtils.removeMarknadsomraden(params.regioner);
        this.busyService.addWork(this.produktinstruktionSearchService.search(params)).subscribe();

        this.webinstallningarService.set<IProduktinstruktionSearchModel>(this.wik, this.searchParms);
    }

    private copyWebbInstallningToSearchObject(searchModel: IProduktinstruktionSearchModel) {
        if (!searchModel) {
            return;
        }

        Object.keys(searchModel).forEach(key => this.searchParms[key] = searchModel[key]);
    }

    private createEmptySearch() {
        this.searchParms = {
            sokText: "",
            sortiment: "",
            handelssortiment: "",
            mottagningsplats: "",
            regioner: [],
            tradslag: [],
            aktiv: undefined
        };
    }

    private mapFromForm() {
        this.searchParms.sokText = this.form.get("sokText").value;
        this.searchParms.sortiment = this.form.get("sortiment").value;
        this.searchParms.handelssortiment = this.form.get("handelssortiment").value;
        this.searchParms.mottagningsplats = this.form.get("mottagningsplats").value;
        this.searchParms.tradslag = this.form.get("tradslag").value;
        this.searchParms.regioner = this.form.get("regioner").value;
        this.searchParms.aktiv = this.formatService.stringToBoolean(this.form.get("aktiv").value);
    }
}
